import { IMAGE_MAX_BYTES, IMAGE_MAX_DIMENSION } from '@plane/shared';

export interface ImagemPreparada {
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
}

/** Qualidade do WebP. 0,82 mantém foto de marcenaria sem artefato visível. */
const QUALIDADE = 0.82;

/**
 * Redimensiona e converte a foto **no navegador**, antes de enviar.
 *
 * Fazer isso aqui e não no servidor evita o `sharp`, que é módulo nativo — a
 * mesma classe de dependência que já custou a migração do monorepo inteiro
 * quando o `postinstall` do esbuild falhou no build da Hostinger. De quebra,
 * sobe 200 KB em vez dos 6 MB que sai de um celular.
 *
 * O maior lado é parâmetro porque o fundo do banner precisa de mais resolução
 * que foto de galeria: ele ocupa a tela inteira, e 1600 esticado para 1920 fica
 * visivelmente mole.
 *
 * Isto é conveniência, não validação: quem chamar a API direto não passa por
 * aqui. O servidor impõe o teto de bytes e o tipo por conta própria.
 */
export async function prepararImagem(
  arquivo: File,
  maiorLado: number = IMAGE_MAX_DIMENSION,
): Promise<ImagemPreparada> {
  const bitmap = await createImageBitmap(arquivo);

  try {
    // Só reduz. Ampliar uma foto pequena não acrescenta detalhe, só peso.
    const escala = Math.min(1, maiorLado / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * escala));
    const height = Math.max(1, Math.round(bitmap.height * escala));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const contexto = canvas.getContext('2d');

    if (!contexto) throw new Error('Não foi possível processar a imagem neste navegador.');

    contexto.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolver) => {
      canvas.toBlob(resolver, 'image/webp', QUALIDADE);
    });

    if (!blob) throw new Error('Não foi possível converter a imagem.');

    if (blob.size > IMAGE_MAX_BYTES) {
      const mb = (IMAGE_MAX_BYTES / 1024 / 1024).toFixed(0);

      throw new Error(`A imagem ficou acima de ${mb} MB mesmo depois de reduzida. Use outra foto.`);
    }

    return { blob, width, height };
  } finally {
    // Libera a memória do bitmap mesmo se a conversão falhar; uma foto de
    // celular ocupa dezenas de megabytes descomprimida.
    bitmap.close();
  }
}
